"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, Mail, Lock, AlertTriangle, Loader2 } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";
import { useAuthStore } from "@/stores/authStore";
import { userHasPermission } from "@/hooks/usePermissions";
import { landingHref } from "@/components/layout/nav-model";

const MONO_LABEL = "text-[13px] font-medium tracking-[-0.01em] text-muted";

export function LoginForm({ dict, lang }: { dict: Dictionary; lang: Locale }) {
  const router = useRouter();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = dict.auth.login.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = dict.auth.login.errors.emailInvalid;
    }

    if (!formData.password) {
      newErrors.password = dict.auth.login.errors.passwordRequired;
    } else if (formData.password.length < 6) {
      newErrors.password = dict.auth.login.errors.passwordMin;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const user = await login(formData.email, formData.password);
      // Land on the first zone this role can open. /dashboard reads
      // company-wide statistics, so a door or PR account would hit a 403 there.
      const href = landingHref({
        hasPermission: (p) => userHasPermission(user, p),
        isSystemAdmin: Boolean(
          user.isSystemAdmin || user.role === "SYSTEM_ADMIN",
        ),
        role: user.role,
      });
      router.push(`/${lang}${href}`);
    } catch (error) {
      console.error("Login error:", error);
      // Handle ApiError from backend
      let message = dict.auth.login.errors.loginFailed;
      if (error && typeof error === "object" && "message" in error) {
        message = error.message as string;
      } else if (error instanceof Error) {
        message = error.message;
      }
      setErrors({ form: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={dict.auth.login.title}
      description={dict.auth.login.description}
      brand={dict.auth.brand}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.form && (
          <div className="flex items-center gap-2.5 rounded-[10px] border border-error-glow-20 bg-error-glow-12 px-3.5 py-3">
            <AlertTriangle
              className="h-4 w-4 shrink-0 text-error-light"
              aria-hidden
            />
            <span className="text-[13px] font-medium tracking-[-0.005em] text-error-light">
              {errors.form}
            </span>
          </div>
        )}

        <Input
          id="email"
          label={dict.auth.login.email}
          labelClassName={MONO_LABEL}
          type="email"
          placeholder={dict.auth.login.emailPlaceholder}
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={errors.email}
          leftIcon={Mail}
          autoComplete="email"
          disabled={isLoading}
        />

        <div>
          <Input
            id="password"
            label={dict.auth.login.password}
            labelClassName={MONO_LABEL}
            type={showPassword ? "text" : "password"}
            placeholder={dict.auth.login.passwordPlaceholder}
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            error={errors.password}
            leftIcon={Lock}
            rightIcon={showPassword ? EyeOff : Eye}
            onRightIconClick={() => setShowPassword(!showPassword)}
            autoComplete="current-password"
            disabled={isLoading}
          />
          <div className="mt-2.5 flex justify-end">
            <Link
              href={`/${lang}/forgot-password`}
              className="text-[13px] text-muted transition-colors duration-150 hover:text-brand-light"
            >
              {dict.auth.login.forgotPassword}
            </Link>
          </div>
        </div>

        <Button
          type="submit"
          size="lg"
          className="mt-2 w-full disabled:cursor-default disabled:opacity-100"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {dict.auth.login.submitting}
            </>
          ) : (
            dict.auth.login.submit
          )}
        </Button>

        <p className="pt-1 text-center text-sm text-muted">
          {dict.auth.login.noAccount}{" "}
          <Link
            href={`/${lang}/trial`}
            className="font-medium text-primary-light transition-colors duration-150 hover:text-brand-lite"
          >
            {dict.auth.login.createAccount}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
